/**
 * Blog API client for GeoStays.
 * Handles auth token resolution across customer session / host id / admin JWT.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api/blogs`;

// Best-effort resolve the current caller's token. Falls back to null (public reads).
export const getAuthToken = () => {
  return (
    localStorage.getItem('session_token') ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('hostToken') ||
    null
  );
};

export const getCurrentAuthorRole = () => {
  if (localStorage.getItem('adminToken')) return 'admin';
  if (localStorage.getItem('session_token')) return 'user';
  if (localStorage.getItem('hostToken')) return 'host';
  return null;
};

const authHeaders = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const blogsAPI = {
  list: async (params = {}) => {
    const { data } = await axios.get(`${BASE}/`, { params });
    return data;
  },
  categories: async () => {
    const { data } = await axios.get(`${BASE}/categories`);
    return data;
  },
  featured: async () => {
    const { data } = await axios.get(`${BASE}/featured`);
    return data;
  },
  related: async (slug) => {
    const { data } = await axios.get(`${BASE}/${slug}/related`);
    return data;
  },
  getBySlug: async (slug) => {
    const { data } = await axios.get(`${BASE}/${slug}`);
    return data;
  },
  mine: async () => {
    const { data } = await axios.get(`${BASE}/mine`, { headers: authHeaders() });
    return data;
  },
  create: async (payload) => {
    const { data } = await axios.post(`${BASE}/`, payload, { headers: authHeaders() });
    return data;
  },
  update: async (id, payload) => {
    const { data } = await axios.put(`${BASE}/${id}`, payload, { headers: authHeaders() });
    return data;
  },
  remove: async (id) => {
    const { data } = await axios.delete(`${BASE}/${id}`, { headers: authHeaders() });
    return data;
  },
  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${BASE}/upload-image`, fd, {
      headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};

// Admin blog moderation
export const adminBlogsAPI = {
  list: async (status = 'pending') => {
    const token = localStorage.getItem('adminToken');
    const { data } = await axios.get(`${BACKEND_URL}/api/admin/blogs`, { params: { token, status } });
    return data;
  },
  get: async (id) => {
    const token = localStorage.getItem('adminToken');
    const { data } = await axios.get(`${BACKEND_URL}/api/admin/blogs/${id}`, { params: { token } });
    return data;
  },
  approve: async (id) => {
    const token = localStorage.getItem('adminToken');
    const { data } = await axios.post(`${BACKEND_URL}/api/admin/blogs/${id}/approve`, { token });
    return data;
  },
  reject: async (id, reason) => {
    const token = localStorage.getItem('adminToken');
    const { data } = await axios.post(`${BACKEND_URL}/api/admin/blogs/${id}/reject`, { token, reason });
    return data;
  },
  remove: async (id) => {
    const token = localStorage.getItem('adminToken');
    const { data } = await axios.delete(`${BACKEND_URL}/api/admin/blogs/${id}`, { params: { token } });
    return data;
  },
  feature: async (id) => {
    const token = localStorage.getItem('adminToken');
    const { data } = await axios.post(`${BACKEND_URL}/api/admin/blogs/${id}/feature`, { token });
    return data;
  },
  unfeature: async (id) => {
    const token = localStorage.getItem('adminToken');
    const { data } = await axios.post(`${BACKEND_URL}/api/admin/blogs/${id}/unfeature`, { token });
    return data;
  },
};

export const resolveImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/')) return `${BACKEND_URL}${path}`;
  if (path.startsWith('/uploads/')) return `${BACKEND_URL}/api${path}`;
  return path;
};

export const CATEGORY_LABELS = {
  'destination-guide': 'Destination Guide',
  'trip-experience': 'Trip Experience',
  'travel-tips': 'Travel Tips',
  'homestay-story': 'Homestay Story',
  'food-culture': 'Food & Culture',
};

export const CATEGORY_COLORS = {
  'destination-guide': 'bg-emerald-100 text-emerald-800',
  'trip-experience': 'bg-orange-100 text-orange-800',
  'travel-tips': 'bg-sky-100 text-sky-800',
  'homestay-story': 'bg-violet-100 text-violet-800',
  'food-culture': 'bg-amber-100 text-amber-800',
};
